import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { TriangleAlert } from 'lucide-react';
import { AdminButton, AdminCard } from '../../ui';
import { useCategories } from '../../../../hooks/use-categories/useCategories';
import { ArenaChallenge } from '../../../../api/group/arena.api';
import {
  adminCreateChallenge,
  adminUpdateChallenge,
  ArenaChallengeUpsert,
} from '../../../../api/group/arena-admin.api';

interface Props {
  challenge?: ArenaChallenge | null;
  onDone: () => void;
  onCancel: () => void;
}

// Ô datetime-local nhập giờ Việt Nam (UTC+7). Đổi qua lại bằng cách cộng trừ 7 giờ, không phụ thuộc múi giờ máy.
const VN_OFFSET_MS = 7 * 3600 * 1000;
const utcToVnInput = (iso?: string | null): string => {
  if (!iso) return '';
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const t = new Date(hasTz ? iso : `${iso}Z`).getTime();
  return new Date(t + VN_OFFSET_MS).toISOString().slice(0, 16);
};
const vnInputToIso = (v: string): string => new Date(new Date(`${v}:00Z`).getTime() - VN_OFFSET_MS).toISOString();

// Điểm mặc định của Clan chiến, tự điền khi admin đổi Loại mà chưa sửa các ô điểm.
const CLAN_WAR_POINTS: Record<string, number> = {
  pointsRank1: 300,
  pointsRank2: 200,
  pointsRank3: 150,
  pointsRank4To10: 60,
  pointsParticipation: 15,
};

// Điểm mặc định của thử thách thường, tự điền lại khi admin đổi Loại về Thường mà chưa sửa các ô điểm.
const NORMAL_POINTS: Record<string, number> = {
  pointsRank1: 100,
  pointsRank2: 70,
  pointsRank3: 50,
  pointsRank4To10: 20,
  pointsParticipation: 5,
};

const inputCls = 'adm-input';

const ArenaChallengeForm: React.FC<Props> = ({ challenge, onDone, onCancel }) => {
  const [title, setTitle] = useState(challenge?.title ?? '');
  const [description, setDescription] = useState(challenge?.description ?? '');
  const [rules, setRules] = useState(challenge?.rules ?? '');
  const [startAt, setStartAt] = useState(utcToVnInput(challenge?.startAt));
  const [submitDeadline, setSubmitDeadline] = useState(utcToVnInput(challenge?.submitDeadline));
  const [judgingDeadline, setJudgingDeadline] = useState(utcToVnInput(challenge?.judgingDeadline));
  const [num, setNum] = useState<Record<string, number>>({
    teamSizeMin: challenge?.teamSizeMin ?? 3,
    teamSizeMax: challenge?.teamSizeMax ?? 5,
    interactionWeight: challenge?.interactionWeight ?? 50,
    pointsRank1: challenge?.pointsRank1 ?? 100,
    pointsRank2: challenge?.pointsRank2 ?? 70,
    pointsRank3: challenge?.pointsRank3 ?? 50,
    pointsRank4To10: challenge?.pointsRank4To10 ?? 20,
    pointsParticipation: challenge?.pointsParticipation ?? 5,
  });
  const [type, setType] = useState<'NORMAL' | 'CLAN_WAR'>(challenge?.type === 'CLAN_WAR' ? 'CLAN_WAR' : 'NORMAL');
  const [categoryId, setCategoryId] = useState<string>(challenge?.categoryId ?? '');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const { postCategories } = useCategories({ type: 'post' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const setN = (k: string, v: string) => {
    setTouched((p) => ({ ...p, [k]: true }));
    setNum((p) => ({ ...p, [k]: Number(v) }));
  };

  const changeType = (t: 'NORMAL' | 'CLAN_WAR') => {
    setType(t);
    const defaults = t === 'CLAN_WAR' ? CLAN_WAR_POINTS : NORMAL_POINTS;
    setNum((p) => {
      const next = { ...p };
      Object.keys(defaults).forEach((k) => {
        if (!touched[k]) next[k] = defaults[k];
      });
      return next;
    });
  };

  const validate = (): string => {
    if (!title.trim()) return 'Nhập tên thử thách.';
    if (!startAt || !submitDeadline || !judgingDeadline) return 'Chọn đủ ba mốc thời gian.';
    const a = new Date(`${startAt}:00Z`).getTime();
    const b = new Date(`${submitDeadline}:00Z`).getTime();
    const c = new Date(`${judgingDeadline}:00Z`).getTime();
    if (!(a < b && b < c)) return 'Mốc thời gian phải theo thứ tự: bắt đầu, hạn nộp bài, hạn chấm.';
    if (num.teamSizeMin < 1 || num.teamSizeMax < num.teamSizeMin) return 'Số thành viên tối đa phải từ mức tối thiểu trở lên.';
    if (num.interactionWeight < 0 || num.interactionWeight > 100) return 'Tỉ trọng tương tác từ 0 đến 100.';
    if (num.pointsRank1 < 0 || num.pointsRank2 < 0 || num.pointsRank3 < 0 || num.pointsRank4To10 < 0 || num.pointsParticipation < 0)
      return 'Điểm theo hạng không được âm.';
    return '';
  };

  const save = async () => {
    const err = validate();
    setError(err);
    if (err) return;
    const body: ArenaChallengeUpsert = {
      title: title.trim(),
      description,
      rules,
      startAt: vnInputToIso(startAt),
      submitDeadline: vnInputToIso(submitDeadline),
      judgingDeadline: vnInputToIso(judgingDeadline),
      ...(num as any),
      type,
      categoryId: categoryId || null,
    };
    try {
      setSaving(true);
      if (challenge) await adminUpdateChallenge(challenge.id, body);
      else await adminCreateChallenge(body);
      toast.success('Đã lưu thử thách.');
      onDone();
    } catch (e: any) {
      toast.error(e?.message || 'Không lưu được thử thách.');
    } finally {
      setSaving(false);
    }
  };

  const numField = (k: string, label: string) => (
    <label className="adm-field">
      <span>{label}</span>
      <input type="number" className={inputCls} value={num[k]} onChange={(e) => setN(k, e.target.value)} />
    </label>
  );

  return (
    <AdminCard title={challenge ? 'Sửa thử thách' : 'Thử thách mới'} className="adm-stack">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="adm-field">
          <span>Loại</span>
          <select
            aria-label="Loại"
            className="adm-select"
            value={type}
            onChange={(e) => changeType(e.target.value as 'NORMAL' | 'CLAN_WAR')}
          >
            <option value="NORMAL">Thử thách thường</option>
            <option value="CLAN_WAR">Clan chiến</option>
          </select>
        </label>
        <label className="adm-field">
          <span>Chuyên mục</span>
          <select aria-label="Chuyên mục" className="adm-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Không giới hạn</option>
            {(postCategories || []).map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="adm-field">
        <span>Tên thử thách</span>
        <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="adm-field">
        <span>Mô tả</span>
        <textarea className="adm-textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <label className="adm-field">
        <span>Thể lệ</span>
        <textarea className="adm-textarea" rows={4} value={rules} onChange={(e) => setRules(e.target.value)} />
      </label>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="adm-field">
          <span>Bắt đầu (giờ Việt Nam)</span>
          <input type="datetime-local" className={inputCls} value={startAt} onChange={(e) => setStartAt(e.target.value)} />
        </label>
        <label className="adm-field">
          <span>Hạn nộp bài (giờ Việt Nam)</span>
          <input
            type="datetime-local"
            className={inputCls}
            value={submitDeadline}
            onChange={(e) => setSubmitDeadline(e.target.value)}
          />
        </label>
        <label className="adm-field">
          <span>Hạn chấm (giờ Việt Nam)</span>
          <input
            type="datetime-local"
            className={inputCls}
            value={judgingDeadline}
            onChange={(e) => setJudgingDeadline(e.target.value)}
          />
        </label>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {numField('teamSizeMin', 'Thành viên tối thiểu')}
        {numField('teamSizeMax', 'Thành viên tối đa')}
        {numField('interactionWeight', 'Tỉ trọng tương tác (%)')}
        {numField('pointsRank1', 'Điểm hạng 1')}
        {numField('pointsRank2', 'Điểm hạng 2')}
        {numField('pointsRank3', 'Điểm hạng 3')}
        {numField('pointsRank4To10', 'Điểm hạng 4 đến 10')}
        {numField('pointsParticipation', 'Điểm tham gia')}
      </div>
      {error && (
        <div className="adm-strip adm-strip--danger" role="alert">
          <TriangleAlert size={20} className="adm-strip__icon" />
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <AdminButton variant="outline" onClick={onCancel}>
          Quay lại
        </AdminButton>
        <AdminButton disabled={saving} onClick={save}>
          Lưu
        </AdminButton>
      </div>
    </AdminCard>
  );
};

export default ArenaChallengeForm;
